import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import PageWrapper from '../layout/PageWrapper';
import AnimatedBackground from '../layout/AnimatedBackground';

interface GalleryImage {
  id: number;
  src: string;
  alt: string;
  title: string;
  category: 'classroom' | 'activities' | 'facilities' | 'meals' | 'outdoor';
}

export default function PhotoGallery() {
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const galleryImages: GalleryImage[] = [
    {
      id: 1,
      src: '/images/gallery/frontBuilding.jpg',
      alt: 'Front of the Harbourside Children\'s Center building',
      title: 'Front Building',
      category: 'facilities'
    },
    {
      id: 2,
      src: '/images/gallery/Oureducators.jpeg',
      alt: 'Our educators with students',
      title: 'Our Educators',
      category: 'activities'
    },
    {
      id: 3,
      src: '/images/gallery/Oureducators2.jpeg',
      alt: 'Educators supporting student learning',
      title: 'Educators in Action',
      category: 'activities'
    },
    {
      id: 4,
      src: '/images/gallery/playground1.jpg',
      alt: 'Children playing on the playground',
      title: 'Playground Fun 1',
      category: 'outdoor'
    },
    {
      id: 5,
      src: '/images/gallery/playground2.jpg',
      alt: 'Kids enjoying outdoor play area',
      title: 'Playground Fun 2',
      category: 'outdoor'
    },
    {
      id: 6,
      src: '/images/gallery/playground3.jpg',
      alt: 'Play structures in outdoor area',
      title: 'Playground Fun 3',
      category: 'outdoor'
    },
    {
      id: 7,
      src: '/images/gallery/studentartproject.jpg',
      alt: 'Student art project display',
      title: 'Student Art Project 1',
      category: 'activities'
    },
    {
      id: 8,
      src: '/images/gallery/studentartproject2.jpg',
      alt: 'Creative artwork by students',
      title: 'Student Art Project 2',
      category: 'activities'
    },
    {
      id: 9,
      src: '/images/gallery/studentartproject3.jpg',
      alt: 'Group art activity result',
      title: 'Student Art Project 3',
      category: 'activities'
    },
    {
      id: 10,
      src: '/images/gallery/ricemeal.jpg',
      alt: 'Healthy rice meal served at the center',
      title: 'Healthy Rice Meal',
      category: 'meals'
    },
    {
      id: 11,
      src: '/images/gallery/veggieplates.jpg',
      alt: 'Assortment of vegetable plates',
      title: 'Veggie Plates',
      category: 'meals'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Photos', color: 'bg-dblue' },
    { id: 'classroom', name: 'Classroom', color: 'bg-blue-500' },
    { id: 'activities', name: 'Activities', color: 'bg-yellow-500' },
    { id: 'facilities', name: 'Facilities', color: 'bg-green-500' },
    { id: 'meals', name: 'Meals', color: 'bg-orange-500' },
    { id: 'outdoor', name: 'Outdoor', color: 'bg-purple-500' }
  ];

  const filteredImages = selectedCategory === 'all' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === selectedCategory);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { duration: 0.5 }
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.3 }
    },
    exit: { 
      opacity: 0, 
      scale: 0.8,
      transition: { duration: 0.2 }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AnimatedBackground type="floating-shapes" className="fixed inset-0 -z-10" />
      
      <PageWrapper>
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-dblue mb-4">
            Photo Gallery
          </h1>
          <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
            Take a glimpse into daily life at Harbourside Children's Center. See our beautiful facilities, 
            engaging activities, and the joyful moments that make our center special.
          </p>
        </motion.div>

        {/* Category Filter Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-3 mb-12"
        >
          {categories.map((category) => (
            <motion.button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-6 py-3 rounded-full font-semibold text-white transition-all duration-300 hover:shadow-lg ${
                selectedCategory === category.id 
                  ? category.color + ' shadow-md' 
                  : 'bg-gray-400 hover:bg-gray-500'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {category.name}
            </motion.button>
          ))}
        </motion.div>

        {/* Photo Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16"
        >
          <AnimatePresence>
            {filteredImages.map((image) => (
              <motion.div
                key={image.id}
                layout
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit={{ opacity: 0, scale: 0.8 }}
                className="group relative bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 cursor-pointer"
                onClick={() => setSelectedImage(image)}
              >
                <div className="aspect-square overflow-hidden">
                  <motion.img
                    src={image.src}
                    alt={image.alt}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    whileHover={{ scale: 1.05 }}
                  />
                </div>
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-white font-bold text-lg mb-2">{image.title}</h3>
                    <div className="flex items-center text-white/80">
                      <div className={`w-3 h-3 rounded-full mr-2 ${categories.find(c => c.id === image.category)?.color || 'bg-gray-400'}`}></div>
                      <span className="text-sm capitalize">{image.category}</span>
                    </div>
                  </div>
                </div>

                {/* Hover Icon */}
                <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  </svg>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Additional Info Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white rounded-2xl shadow-lg p-8 text-center"
        >
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-dblue mb-6">Visit Us Today</h2>
            <p className="text-gray-600 leading-relaxed mb-8">
              We invite you to schedule a tour of our facility to see these spaces in person. 
              Our dedicated staff will be happy to show you around and answer any questions 
              about our programs and approach to early childhood education.
            </p>
            <motion.div
              className="inline-flex space-x-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <motion.a
                href="/enrollment"
                className="bg-yellow-500 text-black px-8 py-4 rounded-full font-semibold hover:bg-yellow-600 transition-colors duration-300 shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Schedule a Tour
              </motion.a>
              <motion.a
                href="/contact-us"
                className="bg-dblue text-white px-8 py-4 rounded-full font-semibold hover:bg-blue-700 transition-colors duration-300 shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Contact Us
              </motion.a>
            </motion.div>
          </div>
        </motion.div>
      </PageWrapper>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="relative max-w-4xl max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 z-10 bg-white/20 backdrop-blur-sm rounded-full p-2 text-white hover:bg-white/30 transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="relative">
                <img
                  src={selectedImage.src}
                  alt={selectedImage.alt}
                  loading="eager"
                  decoding="async"
                  className="w-full max-h-[80vh] object-contain"
                />
                
                {/* Image Info */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                  <h3 className="text-white font-bold text-2xl mb-2">{selectedImage.title}</h3>
                  <div className="flex items-center text-white/80">
                    <div className={`w-3 h-3 rounded-full mr-2 ${categories.find(c => c.id === selectedImage.category)?.color || 'bg-gray-400'}`}></div>
                    <span className="capitalize">{selectedImage.category}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

